import type { Notification } from "./notifications.types";

/** Label used when a notification cannot be attributed to a known product. */
export const NOTIFICATION_FALLBACK_DISPLAY_NAME = "OpenGov";

/**
 * Stable filter keys for the products that can originate a notification.
 *
 * These are the values the product filter stores and compares against, so they
 * are deliberately short and independent of the URNs/product keys the Beacon
 * payload happens to use.
 */
export enum NotificationProductKey {
  Budget = "budget",
  Performance = "performance",
  AdvanceWorkflows = "advanceWorkflows",
  /** Sentinel bucket for notifications with no recognisable subject or product. */
  OpenGov = "opengov",
}

export interface NotificationProductDefinition {
  readonly key: NotificationProductKey;
  readonly displayName: string;
  /** Resource subject URN prefixes owned by this product (`Notification.subject`). */
  readonly subjectUrns: readonly string[];
  /** Beacon product keys owned by this product (`Notification.product`). */
  readonly productKeys: readonly string[];
}

/**
 * Single source of truth for notification → product attribution.
 *
 * A notification is attributed by its `subject` resource URN when we recognise
 * one, and falls back to its `product` key otherwise. Matching both means the
 * chip in the navbar drawer, the chip on the notifications page, and the page's
 * product filter options can never drift apart.
 *
 *   Budget       urn:opengov:budget, urn:opengov:budgetRequests,   product: budget, budgetRequests,
 *                urn:opengov:advanceWorkflows                              advanceWorkflows
 *   Performance  urn:opengov:perfmgmt                             product: perfmgmt
 *
 * Budget owns everything under `urn:opengov:budget:` — advance workflows
 * included, whether they arrive nested under budget
 * (`urn:opengov:budget:advanceworkflows`) or as their own top-level
 * subject/product. Advance workflows is a Budget feature, not a product.
 */
export const NOTIFICATION_PRODUCTS: readonly NotificationProductDefinition[] = [
  {
    key: NotificationProductKey.Budget,
    displayName: "Budget",
    subjectUrns: ["urn:opengov:budget", "urn:opengov:budgetRequests", "urn:opengov:advanceWorkflows"],
    productKeys: ["budget", "budgetRequests", "advanceWorkflows"],
  },
  {
    key: NotificationProductKey.Performance,
    displayName: "Performance",
    subjectUrns: ["urn:opengov:perfmgmt"],
    productKeys: ["perfmgmt"],
  },
];

/**
 * A subject may carry a resource id (`urn:opengov:budgetRequests/42`); only the
 * URN ahead of the first `/` identifies the product.
 *
 * Lower-cased because producers are inconsistent about camelCase segments
 * (`advanceWorkflows` vs `advanceworkflows`), and a casing difference should not
 * cost a notification its product chip.
 */
function subjectUrnOf(subject: string): string {
  return subject.split("/")[0].toLowerCase();
}

/**
 * A registered URN matches a subject when it is the subject itself or one of its
 * ancestors at a `:` segment boundary. Requiring the boundary is what stops
 * `urn:opengov:budget` from swallowing `urn:opengov:budgetRequests`.
 */
function matchesSubjectUrn(urn: string, subject: string): boolean {
  const candidate = urn.toLowerCase();
  return subject === candidate || subject.startsWith(`${candidate}:`);
}

/**
 * Resolves by subject, preferring the most specific registered URN so that a
 * future nested product wins over the ancestor it sits under rather than being
 * silently absorbed by it.
 */
function resolveBySubject(subject: string): NotificationProductDefinition | undefined {
  const urn = subjectUrnOf(subject);
  let match: NotificationProductDefinition | undefined;
  let matchSegments = 0;

  NOTIFICATION_PRODUCTS.forEach((product) => {
    product.subjectUrns.forEach((candidate) => {
      const segments = candidate.split(":").length;
      if (segments > matchSegments && matchesSubjectUrn(candidate, urn)) {
        match = product;
        matchSegments = segments;
      }
    });
  });

  return match;
}

function resolveByProductKey(product: string): NotificationProductDefinition | undefined {
  const key = product.toLowerCase();
  return NOTIFICATION_PRODUCTS.find((candidate) => candidate.productKeys.some((k) => k.toLowerCase() === key));
}

export interface ResolvedNotificationProduct {
  /** Filter key, for comparing against the product filter selection. */
  key: NotificationProductKey;
  /** Human-readable label for the chip. */
  displayName: string;
}

const OPENGOV_PRODUCT: ResolvedNotificationProduct = {
  key: NotificationProductKey.OpenGov,
  displayName: NOTIFICATION_FALLBACK_DISPLAY_NAME,
};

/**
 * Attributes a notification to a product using its `subject` URN, falling back
 * to its `product` key. Unrecognised notifications resolve to "OpenGov".
 */
export function resolveNotificationProduct(
  notification: Pick<Notification, "subject" | "product">,
): ResolvedNotificationProduct {
  const bySubject = notification.subject ? resolveBySubject(notification.subject) : undefined;
  const match = bySubject ?? (notification.product ? resolveByProductKey(notification.product) : undefined);

  if (!match) return OPENGOV_PRODUCT;
  return { key: match.key, displayName: match.displayName };
}

/**
 * Options for the notifications page product filter — one per known product.
 *
 * The "OpenGov" fallback is deliberately not an option: it is a catch-all for
 * unattributable notifications, not a product users would filter by.
 */
export function getAvailableNotificationProducts(): Array<{ key: NotificationProductKey; displayName: string }> {
  return NOTIFICATION_PRODUCTS.map(({ key, displayName }) => ({ key, displayName }));
}

/**
 * Exact-match map of known notification subject URNs to their display names.
 *
 * @deprecated Prefer {@link resolveNotificationProduct}, which also matches on
 * `Notification.product` and handles subject URNs that carry a resource id.
 */
export const NOTIFICATION_SUBJECT_DISPLAY_NAMES: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(
    NOTIFICATION_PRODUCTS.flatMap((product) => product.subjectUrns.map((urn) => [urn, product.displayName])),
  ),
);

/**
 * Returns a human-readable label for a notification subject URN.
 * Unrecognised or absent subjects fall back to "OpenGov".
 */
export function getSubjectDisplayName(subject?: string): string {
  if (!subject) return NOTIFICATION_FALLBACK_DISPLAY_NAME;
  return resolveBySubject(subject)?.displayName ?? NOTIFICATION_FALLBACK_DISPLAY_NAME;
}

function pluralUnit(count: number, singular: string, plural: string): string {
  if (count === 1) return singular;
  return plural;
}

const TIME_OF_DAY: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit", hour12: true };
const MONTH_DAY: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
const MONTH_DAY_YEAR: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function startOfLocalDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * Whole calendar days between two instants, in local time.
 *
 * Deliberately compares midnights rather than elapsed hours: 11:59 PM yesterday
 * is "1 day ago" even though only a minute has passed. Rounding absorbs the
 * 23/25-hour days introduced by daylight saving transitions.
 */
function calendarDaysBetween(from: Date, to: Date): number {
  return Math.round((startOfLocalDay(to) - startOfLocalDay(from)) / MS_PER_DAY);
}

/**
 * Feed-style timestamp for a notification.
 *
 *   today, < 60s                    Just now
 *   today, 1-59 minutes             {n} minute(s) ago
 *   today, 1-23 hours               {n} hour(s) ago
 *   1 calendar day ago              Yesterday at 11:59 PM
 *   2-6 calendar days ago           {n} days ago
 *   7-29 calendar days ago          Mar 12 at 2:59 PM
 *   30+ days, same calendar year    Mar 12
 *   earlier calendar year           Mar 12, 2025
 *
 * The first three buckets are gated on the event falling on *today*, so a
 * notification from 11:00 PM last night reads "Yesterday at 11:00 PM" rather
 * than "8 hours ago".
 */
export function formatRelativeTime(timestamp: string): string {
  const now = new Date();
  const date = new Date(timestamp);
  const calendarDays = calendarDaysBetween(date, now);

  // Same calendar day (or a future timestamp from clock skew): report elapsed time.
  if (calendarDays <= 0) {
    const diffSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffSeconds < 60) return "Just now";

    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) return pluralUnit(diffMinutes, "1 minute ago", `${diffMinutes} minutes ago`);

    const diffHours = Math.floor(diffMinutes / 60);
    return pluralUnit(diffHours, "1 hour ago", `${diffHours} hours ago`);
  }

  if (calendarDays === 1) return `Yesterday at ${date.toLocaleTimeString(undefined, TIME_OF_DAY)}`;
  if (calendarDays < 7) return `${calendarDays} days ago`;
  if (calendarDays < 30) {
    return `${date.toLocaleDateString(undefined, MONTH_DAY)} at ${date.toLocaleTimeString(undefined, TIME_OF_DAY)}`;
  }
  if (date.getFullYear() === now.getFullYear()) return date.toLocaleDateString(undefined, MONTH_DAY);
  return date.toLocaleDateString(undefined, MONTH_DAY_YEAR);
}

export function formatFullDateTime(timestamp: string): string {
  return new Date(timestamp).toLocaleString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}
